import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, Share2, ArrowRight } from 'lucide-react';
import { getAllTypes, getTypeByCode } from '../data/typesData';
import { useLanguage } from '../contexts/LanguageContext';
import StarChart from '../components/viz/StarChart';
import DisplayHeading from '../components/primitives/DisplayHeading';
import HairlineDivider from '../components/primitives/HairlineDivider';
import MonoCaption from '../components/primitives/MonoCaption';

/* =====================================================
 * Axis grouping (同 TypesListPage)
 * ===================================================== */

const AXIS_FROM_PREFIX = {
  AC: 'activation',
  JD: 'judgment',
  CH: 'choice',
  RS: 'resonance',
  AW: 'awareness',
};
const INTEGRATION_CODES = new Set(['BLNC', 'CMPL', 'ADPT']);

const AXIS_COLORS = {
  activation:  '#F4B860',
  judgment:    '#6B8CCB',
  choice:      '#9C7BC9',
  resonance:   '#D08A8A',
  awareness:   '#7FB89A',
  integration: '#E6C77E',
};

const AXIS_LABEL = {
  activation:  { ja: '起動',   en: 'Activation' },
  judgment:    { ja: '判断',   en: 'Judgment'   },
  choice:      { ja: '選択',   en: 'Choice'     },
  resonance:   { ja: '共鳴',   en: 'Resonance'  },
  awareness:   { ja: '自覚',   en: 'Awareness'  },
  integration: { ja: '統合',   en: 'Integration'},
};

const getGroupOf = (code) => {
  if (INTEGRATION_CODES.has(code)) return 'integration';
  return AXIS_FROM_PREFIX[code.slice(0, 2)] || 'integration';
};

/* =====================================================
 * Layout
 * ===================================================== */

const Page = styled.div`
  position: relative;
  min-height: 100vh;
  background: ${p => p.theme.colors.bgPrimary};
  color: ${p => p.theme.colors.text};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 40% at 30% 20%, rgba(156,123,201,0.07), transparent 60%),
      radial-gradient(ellipse 50% 35% at 75% 80%, rgba(107,140,203,0.05), transparent 65%);
    pointer-events: none;
    z-index: 0;
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  max-width: ${p => p.theme.containers.narrow};
  margin: 0 auto;
  padding: 56px 24px 96px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    padding: 36px 16px 64px;
  }
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: ${p => p.theme.colors.textMuted};
  font-family: ${p => p.theme.fonts.mono};
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 8px 4px 8px 0;
  margin-bottom: 28px;
  transition: color 0.2s;

  &:hover { color: ${p => p.theme.colors.starGold}; }
`;

/* =====================================================
 * Hero (Type identity)
 * ===================================================== */

const Hero = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: 32px 24px 40px;
  border-bottom: 1px solid ${p => p.theme.colors.lineFaint};
  margin-bottom: 48px;

  /* 上辺に軸色のヘアライン */
  border-top: 2px solid ${p => p.$accent};
  border-radius: ${p => p.theme.borderRadius.md} ${p => p.theme.borderRadius.md} 0 0;
  background:
    radial-gradient(ellipse at top, ${p => p.$accent}15, transparent 65%),
    ${p => p.theme.colors.gradCard};
`;

const StarHero = styled.div`
  width: 160px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

const TypeCode = styled.div`
  font-family: ${p => p.theme.fonts.mono};
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.32em;
  color: ${p => p.theme.colors.starGold};
`;

const TypeName = styled.h1`
  margin: 0;
  font-family: ${p => p.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: clamp(2.2rem, 5vw, 3rem);
  line-height: 1.1;
  color: ${p => p.theme.colors.text};
`;

const TypeArche = styled.div`
  font-family: ${p => p.theme.fonts.body};
  font-size: 1rem;
  color: ${p => p.theme.colors.textSecondary};
  letter-spacing: 0.04em;
`;

const TypeTag = styled.p`
  margin: 8px 0 0;
  max-width: 36ch;
  font-size: 0.95rem;
  line-height: 1.85;
  color: ${p => p.theme.colors.textSecondary};
  font-style: italic;
  font-family: ${p => p.theme.fonts.display};
`;

const AxisBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: ${p => p.theme.colors.bgPrimary};
  border: 1px solid ${p => p.theme.colors.lineFaint};
  border-radius: ${p => p.theme.borderRadius.full};
  font-family: ${p => p.theme.fonts.mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.textMuted};
  margin-top: 8px;
`;

const AxisDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${p => p.$color};
  box-shadow: 0 0 8px ${p => p.$color}66;
`;

/* =====================================================
 * Sections
 * ===================================================== */

const SectionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionCard = styled(motion.div)`
  position: relative;
  padding: 28px 28px 30px;
  background: ${p => p.theme.colors.gradCard};
  border: 1px solid ${p => p.theme.colors.lineFaint};
  border-radius: ${p => p.theme.borderRadius.md};

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    padding: 22px 20px 24px;
  }
`;

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 14px;
`;

const SectionNumber = styled.span`
  font-family: ${p => p.theme.fonts.mono};
  font-size: 0.7rem;
  letter-spacing: 0.2em;
  color: ${p => p.theme.colors.starGold};
  opacity: 0.85;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-family: ${p => p.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: 1.25rem;
  color: ${p => p.theme.colors.text};
`;

const SectionBody = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 2;
  color: ${p => p.theme.colors.textSecondary};
  white-space: pre-wrap;
`;

/* =====================================================
 * Related types
 * ===================================================== */

const RelatedStack = styled.div`
  margin-top: 56px;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 20px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const RelatedCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: ${p => p.theme.colors.gradCard};
  border: 1px solid ${p => p.theme.colors.lineFaint};
  border-radius: ${p => p.theme.borderRadius.md};
  text-decoration: none;
  transition: border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${p => p.theme.colors.lineGold};
    transform: translateY(-2px);
  }
`;

const RelatedInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const RelatedCode = styled.span`
  font-family: ${p => p.theme.fonts.mono};
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  color: ${p => p.theme.colors.starGold};
`;

const RelatedName = styled.span`
  font-family: ${p => p.theme.fonts.display};
  font-style: italic;
  font-size: 0.95rem;
  color: ${p => p.theme.colors.text};
  line-height: 1.3;
`;

const RelatedArche = styled.span`
  font-size: 0.72rem;
  color: ${p => p.theme.colors.textMuted};
  margin-top: 1px;
`;

/* =====================================================
 * Footer actions
 * ===================================================== */

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 56px;
  flex-wrap: wrap;
`;

const CtaPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 13px 26px;
  background: ${p => p.theme.colors.gradGold};
  color: #1A1408;
  text-decoration: none;
  border-radius: ${p => p.theme.borderRadius.full};
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 0.04em;
  border: 1px solid rgba(230,199,126,0.55);
  box-shadow: ${p => p.theme.shadows.glowSoft};
  cursor: pointer;
  font-family: ${p => p.theme.fonts.body};
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${p => p.theme.shadows.glow};
  }
`;

const CtaGhost = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 22px;
  background: transparent;
  color: ${p => p.theme.colors.textSecondary};
  border: 1px solid ${p => p.theme.colors.lineSoft};
  border-radius: ${p => p.theme.borderRadius.full};
  font-weight: 500;
  font-size: 0.88rem;
  letter-spacing: 0.04em;
  cursor: pointer;
  font-family: ${p => p.theme.fonts.body};
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: ${p => p.theme.colors.starGold};
    border-color: ${p => p.theme.colors.lineGold};
  }
`;

const ErrorWrap = styled.div`
  text-align: center;
  padding: 96px 24px;
  color: ${p => p.theme.colors.textSecondary};
`;

/* =====================================================
 * Component
 * ===================================================== */

const TypeDetailPage = () => {
  const { typeCode } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLanguage();

  const type = getTypeByCode(typeCode, lang);

  // 同じ軸グループの他 type を最大 3 件
  const related = useMemo(() => {
    if (!type) return [];
    const group = getGroupOf(type.code);
    return getAllTypes(lang)
      .filter(ty => ty.code !== type.code && getGroupOf(ty.code) === group)
      .slice(0, 3);
  }, [type, lang]);

  const handleShare = () => {
    if (!type) return;
    if (navigator.share) {
      navigator.share({
        title: `STRUCT CODE — ${type.code} ${type.name}`,
        text: `${type.code} / ${type.name}（${type.archetype}）`,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert(t('URLをコピーしました', 'URL copied to clipboard'));
    }
  };

  if (!type) {
    return (
      <Page>
        <Container>
          <ErrorWrap>
            <DisplayHeading
              title={t('かたちが見つかりませんでした。', 'This form was not found.')}
              italic
              align="center"
              size="md"
            />
            <div style={{ height: 24 }} />
            <CtaGhost onClick={() => navigate('/types')}>
              <ArrowLeft size={16} />
              {t('24 のかたちへ', 'Back to 24 forms')}
            </CtaGhost>
          </ErrorWrap>
        </Container>
      </Page>
    );
  }

  const group = getGroupOf(type.code);
  const accent = AXIS_COLORS[group];
  const axisLabel = AXIS_LABEL[group][lang === 'en' ? 'en' : 'ja'];

  // v3.0: 6 項目 → 13 項目。占星術の座から導出した構造記述を追加した。
  // 順序は「構造そのもの → 具体的な現れ方 → 読み解き」。
  // content が空の項目は下の filter で落ちるので、旧データでも壊れない。
  const sections = [
    { key: 'summary',            title: t('概要',                     'Overview'),                content: type.summary            },
    { key: 'axis_breakdown',     title: t('5 軸それぞれの働き方',        'The Five Axes'),           content: type.axis_breakdown     },
    { key: 'astrological_seat',  title: t('占星術的な座',              'Astrological Seat'),       content: type.astrological_seat  },
    { key: 'decision_style',     title: t('意思決定のしかた',           'Decision-Making Style'),   content: type.decision_style     },
    { key: 'choice_pattern',     title: t('選びの傾向',                'Choice Pattern'),          content: type.choice_pattern     },
    { key: 'work_style',         title: t('仕事での現れ方',             'At Work'),                 content: type.work_style         },
    { key: 'relation_hint',      title: t('人との響きあい方',           'Interpersonal Dynamics'),  content: type.relation_hint      },
    { key: 'under_pressure',     title: t('負荷がかかったときの反応',     'Under Pressure'),          content: type.under_pressure     },
    { key: 'environment',        title: t('力を発揮しやすい環境',        'Environment'),             content: type.environment        },
    { key: 'risk_note',          title: t('気をつけたいこと',           'Blind Spots'),             content: type.risk_note          },
    { key: 'common_misreading',  title: t('誤解されやすい点',           'Common Misreadings'),      content: type.common_misreading  },
    { key: 'design_gap_reading', title: t('ネイタルとカレントが離れたとき', 'Reading the Design Gap'),  content: type.design_gap_reading },
    { key: 'growth_tip',         title: t('成長の道筋',                'Growth Path'),             content: type.growth_tip         },
  ].filter((s) => s.content);

  return (
    <Page>
      <Container>
        <BackLink onClick={() => navigate('/types')}>
          <ArrowLeft size={14} />
          {t('24 のかたちへ', '24 forms')}
        </BackLink>

        {/* ===== Hero ===== */}
        <Hero
          $accent={accent}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <StarHero>
            <StarChart code={type.code} size={160} color={accent} />
          </StarHero>
          <TypeCode>{type.code}</TypeCode>
          <TypeName>{type.name}</TypeName>
          {type.archetype && <TypeArche>{type.archetype}</TypeArche>}
          {type.tagline && <TypeTag>{type.tagline}</TypeTag>}
          <AxisBadge>
            <AxisDot $color={accent} />
            {axisLabel}
          </AxisBadge>
        </Hero>

        {/* ===== Sections ===== */}
        <SectionStack>
          {sections.map((section, index) => (
            section.content ? (
              <SectionCard
                key={section.key}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.25) }}
              >
                <SectionHead>
                  <SectionNumber>{String(index + 1).padStart(2, '0')}</SectionNumber>
                  <SectionTitle>{section.title}</SectionTitle>
                </SectionHead>
                <SectionBody>{section.content}</SectionBody>
              </SectionCard>
            ) : null
          ))}
        </SectionStack>

        {/* ===== Related types ===== */}
        {related.length > 0 && (
          <RelatedStack>
            <HairlineDivider mark="☾" />
            <DisplayHeading
              eyebrow={t('同じ軸のかたち', 'Same Axis')}
              title={t('近くにいるかたち。', 'Forms nearby.')}
              italic
              align="left"
              size="md"
              as="h2"
            />
            <RelatedGrid>
              {related.map(r => (
                <RelatedCard key={r.code} to={`/types/${r.code}`}>
                  <StarChart code={r.code} size={48} color={accent} faint />
                  <RelatedInfo>
                    <RelatedCode>{r.code}</RelatedCode>
                    <RelatedName>{r.name}</RelatedName>
                    {r.archetype && <RelatedArche>{r.archetype}</RelatedArche>}
                  </RelatedInfo>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </RelatedStack>
        )}

        {/* ===== CTA ===== */}
        <ActionRow>
          <CtaPrimary onClick={() => navigate('/current-diagnosis')}>
            {t('夜空に問いを置く', 'Place a question')}
            <ArrowRight size={16} />
          </CtaPrimary>
          <CtaGhost onClick={handleShare}>
            <Share2 size={14} />
            {t('シェアする', 'Share')}
          </CtaGhost>
        </ActionRow>

        <div style={{ height: 16 }} />
        <div style={{ textAlign: 'center' }}>
          <MonoCaption upper>
            {t('無料 ・ 5 分から 10 分', 'Free · 5–10 min')}
          </MonoCaption>
        </div>
      </Container>
    </Page>
  );
};

export default TypeDetailPage;
